/**
 * FleetOptionsPage · v2.6.7 — driver-facing Fleet Options.
 *
 * Two tabs:
 *   - Fuel Fleet Card · submit a fuel purchase with receipt upload (GridFS, 100 MB)
 *   - Fleet Management · driver's own vehicle info + oil-change history + maintenance status
 *
 * Receipt uploads stream through the existing /api/uploads/driver-doc endpoint
 * which already enforces the 100 MB cap server-side.
 */
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Fuel, Wrench, Upload, Camera, AlertTriangle, CheckCircle2, ShieldAlert, Car, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";

const OIL_TYPES = [
  { value: "full_synthetic", label: "Full Synthetic (6,000 mi interval)" },
  { value: "mixed_synthetic", label: "Mixed Synthetic (3,500 mi interval)" },
  { value: "conventional", label: "Conventional (3,000 mi interval)" },
];

const TIER_BG = {
  green: "bg-emerald-500/10 border-l-4 border-l-emerald-400",
  pink_orange: "bg-orange-300/10 border-l-4 border-l-pink-400",
  orange: "bg-orange-500/10 border-l-4 border-l-orange-400",
  red: "bg-red-500/15 border-l-4 border-l-red-500",
};
const TIER_TEXT = {
  green: "text-emerald-400",
  pink_orange: "text-pink-300",
  orange: "text-orange-300",
  red: "text-red-400",
};

export default function FleetOptionsPage() {
  const [sub, setSub] = useState("fuel");
  return (
    <Layout title="Fleet Options">
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-2 mb-4 h-auto gap-1 p-1">
          <TabsTrigger value="fuel" data-testid="fleet-tab-fuel" className="text-xs sm:text-sm py-2"><Fuel className="h-4 w-4 mr-1.5"/>Fuel Fleet Card</TabsTrigger>
          <TabsTrigger value="vehicle" data-testid="fleet-tab-vehicle" className="text-xs sm:text-sm py-2"><Wrench className="h-4 w-4 mr-1.5"/>Fleet Management</TabsTrigger>
        </TabsList>
        <TabsContent value="fuel"><FuelCardPanel/></TabsContent>
        <TabsContent value="vehicle"><VehicleManagementPanel/></TabsContent>
      </Tabs>
    </Layout>
  );
}

// =========================================================================
// Fuel Fleet Card · driver submission
// =========================================================================
function FuelCardPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [drivers, setDrivers] = useState([]);
  // v2.6.7 batch I · `kind` toggle — when 'business', we hide odometer/place/
  // gallons fields and show a non-fuel expense form (vendor/category/amount).
  const [kind, setKind] = useState("fuel"); // "fuel" | "business"
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    driver_id: "", odometer: "", city: "", state: "OK", place: "",
    price: "", gallons: "", purchase_date: today, notes: "",
    // Business expense fields
    biz_category: "car_wash", biz_description: "", biz_amount: "", biz_vendor: "",
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptFileId, setReceiptFileId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [bizHistory, setBizHistory] = useState([]);

  const reload = () => {
    api.get("/fleet/fuel-cards/mine").then(r => setHistory(r.data.items || [])).catch(()=>{});
    api.get("/fleet/business-expenses/mine").then(r => setBizHistory(r.data.items || [])).catch(()=>{});
  };

  useEffect(() => {
    api.get("/fleet/drivers").then(r => setDrivers(r.data.drivers || [])).catch(()=>{});
    api.get("/auth/me").then(r => { setMe(r.data); setForm(f => ({ ...f, driver_id: r.data.id })); }).catch(()=>{});
    reload();
  }, []);

  const uploadReceipt = async () => {
    if (!receiptFile) return null;
    const fd = new FormData();
    fd.append("file", receiptFile);
    try {
      const { data } = await api.post("/uploads/driver-doc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setReceiptFileId(data.file_id);
      toast.success("Receipt uploaded");
      return data.file_id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receipt upload failed");
      return null;
    }
  };

  const submitFuel = async () => {
    if (!form.driver_id) { toast.error("Pick a driver"); return; }
    if (!form.odometer || !form.price || !form.gallons) { toast.error("Odometer, price and gallons are required"); return; }
    setBusy(true);
    let fileId = receiptFileId;
    if (receiptFile && !fileId) fileId = await uploadReceipt();
    try {
      await api.post("/fleet/fuel-cards", {
        driver_id: form.driver_id,
        odometer: parseFloat(form.odometer),
        city: form.city, state: form.state, place: form.place,
        price: parseFloat(form.price), gallons: parseFloat(form.gallons),
        purchase_date: form.purchase_date, notes: form.notes,
        receipt_file_id: fileId,
      });
      toast.success("Fuel card submitted");
      setForm({ ...form, odometer: "", city: "", state: "OK", place: "", price: "", gallons: "", notes: "" });
      setReceiptFile(null); setReceiptFileId(null); setUploadProgress(0);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const submitBusiness = async () => {
    if (!form.biz_description || !form.biz_amount) { toast.error("Description and amount required"); return; }
    setBusy(true);
    let fileId = receiptFileId;
    if (receiptFile && !fileId) fileId = await uploadReceipt();
    try {
      await api.post("/fleet/business-expenses", {
        category: form.biz_category,
        description: form.biz_description,
        amount: parseFloat(form.biz_amount),
        vendor: form.biz_vendor,
        expense_date: form.purchase_date,
        notes: form.notes,
        receipt_file_id: fileId,
      });
      toast.success("Business expense submitted");
      setForm({ ...form, biz_description: "", biz_amount: "", biz_vendor: "", notes: "" });
      setReceiptFile(null); setReceiptFileId(null); setUploadProgress(0);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const submit = () => kind === "fuel" ? submitFuel() : submitBusiness();

  return (
    <div className="space-y-4" data-testid="fuel-card-panel">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-bold flex items-center gap-2"><Fuel className="h-5 w-5 text-amber-400"/>Submit Expense</h3>
          <div data-testid="expense-kind-select" className="w-56">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100 h-9 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                <SelectItem value="fuel" data-testid="kind-option-fuel">Fuel Expense</SelectItem>
                <SelectItem value="business" data-testid="kind-option-business">Business Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Driver</Label>
            <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="fuel-driver-select"><SelectValue placeholder="Pick driver"/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-60">
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.role}{d.plate_number ? ` · ${d.plate_number}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {kind === "fuel" ? (
            <>
              <Field label="Odometer (mi)" testid="fuel-odometer" type="number" step="0.1" value={form.odometer} onChange={(v) => setForm({ ...form, odometer: v })}/>
              <Field label="Purchase Date" testid="fuel-date" type="date" value={form.purchase_date} onChange={(v) => setForm({ ...form, purchase_date: v })}/>
              <Field label="City" testid="fuel-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })}/>
              <Field label="State (2 letter)" testid="fuel-state" value={form.state} onChange={(v) => setForm({ ...form, state: (v || "").toUpperCase().slice(0, 2) })}/>
              <Field label="Place Fueled Up" testid="fuel-place" value={form.place} onChange={(v) => setForm({ ...form, place: v })} placeholder="e.g. Love's Travel Stop #245"/>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Price ($)" testid="fuel-price" type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })}/>
                <Field label="Gallons" testid="fuel-gallons" type="number" step="0.01" value={form.gallons} onChange={(v) => setForm({ ...form, gallons: v })}/>
              </div>
            </>
          ) : (
            <>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Category</Label>
                <Select value={form.biz_category} onValueChange={(v) => setForm({ ...form, biz_category: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="biz-category"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                    <SelectItem value="car_wash">Car wash</SelectItem>
                    <SelectItem value="parking">Parking</SelectItem>
                    <SelectItem value="toll">Toll</SelectItem>
                    <SelectItem value="air_freshener">Air freshener</SelectItem>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="maintenance_minor">Minor maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Date" testid="biz-date" type="date" value={form.purchase_date} onChange={(v) => setForm({ ...form, purchase_date: v })}/>
              <Field label="Vendor" testid="biz-vendor" value={form.biz_vendor} onChange={(v) => setForm({ ...form, biz_vendor: v })} placeholder="e.g. Quick Wash Express"/>
              <div className="sm:col-span-2">
                <Field label="Description" testid="biz-description" value={form.biz_description} onChange={(v) => setForm({ ...form, biz_description: v })} placeholder="What was this for?"/>
              </div>
              <Field label="Amount ($)" testid="biz-amount" type="number" step="0.01" value={form.biz_amount} onChange={(v) => setForm({ ...form, biz_amount: v })}/>
            </>
          )}

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Receipt photo / file (up to 100 MB)</Label>
            <label className="mt-1 block bg-zinc-950 border border-dashed border-zinc-700 rounded-md px-3 py-4 text-center cursor-pointer hover:border-amber-400/60" data-testid="fuel-receipt-label">
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                data-testid="fuel-receipt-input"
                onChange={(e) => { const f = e.target.files?.[0]; setReceiptFile(f || null); setReceiptFileId(null); setUploadProgress(0); }}
              />
              {receiptFile ? (
                <div className="text-xs text-zinc-300 flex items-center justify-center gap-2"><Camera className="h-4 w-4 text-amber-400"/>{receiptFile.name} · {Math.round(receiptFile.size / 1024)} KB</div>
              ) : (
                <div className="text-xs text-zinc-500"><Upload className="h-4 w-4 inline mr-1"/>Tap to attach receipt</div>
              )}
              {uploadProgress > 0 && uploadProgress < 100 && <div className="text-[10px] text-amber-400 mt-1">Uploading… {uploadProgress}%</div>}
              {receiptFileId && <div className="text-[10px] text-emerald-400 mt-1">✓ Receipt uploaded</div>}
            </label>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes" testid="fuel-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })}/>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="fuel-submit" onClick={submit} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {busy ? "Submitting…" : kind === "fuel" ? "Submit Fuel Card" : "Submit Business Expense"}
          </Button>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <h3 className="font-bold flex items-center gap-2 mb-2"><History className="h-4 w-4 text-amber-400"/>My recent submissions</h3>
        {history.length === 0 ? (
          <div className="text-xs text-zinc-500 py-6 text-center">No fuel-card submissions yet.</div>
        ) : (
          <div className="space-y-1 max-h-80 overflow-auto">
            {history.map(c => (
              <div key={c.id} className={`p-2 rounded text-xs ${TIER_BG[c.color_tier?.tier] || "bg-zinc-950 border border-zinc-800"}`} data-testid={`fuel-row-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold">{c.purchase_date} · {c.place || "—"}</span>
                  <span className={`font-bold ${TIER_TEXT[c.color_tier?.tier] || "text-zinc-200"}`}>{fmtUsd(c.price)} · {c.gallons} gal</span>
                </div>
                <div className="text-[10px] text-zinc-500">{c.city}, {c.state} · {c.odometer} mi · {c.color_tier?.label}{c.color_tier?.flagged ? " · FLAGGED" : ""}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// =========================================================================
// Fleet Management · driver-side vehicle info + oil change tracking
// =========================================================================
function VehicleManagementPanel() {
  const [maint, setMaint] = useState(null);
  const [me, setMe] = useState(null);
  const [oilHistory, setOilHistory] = useState([]);
  const [vehicleForm, setVehicleForm] = useState({ make: "", model: "", year: "", color: "", plate_number: "", vin: "", current_odometer: "" });
  const [oilForm, setOilForm] = useState({ oil_type: "full_synthetic", odometer: "", service_date: new Date().toISOString().slice(0,10), service_location: "", cost: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { const r = await api.get("/auth/me"); setMe(r.data); setVehicleForm({
      make: r.data.vehicle_make || "", model: r.data.vehicle_model || "", year: r.data.vehicle_year || "",
      color: r.data.vehicle_color || "", plate_number: r.data.plate_number || "", vin: r.data.vin || "",
      current_odometer: r.data.current_odometer || "",
    }); } catch (e) { console.warn("FleetOptions /auth/me failed", e?.response?.status || e?.message); }
    try { setMaint((await api.get("/fleet/maintenance/me")).data); } catch (e) { console.warn("FleetOptions maintenance/me failed", e?.response?.status || e?.message); }
    try { setOilHistory((await api.get("/fleet/oil-changes/mine")).data.items || []); } catch (e) { console.warn("FleetOptions oil-changes/mine failed", e?.response?.status || e?.message); }
  };
  useEffect(() => { reload(); }, []);

  const saveVehicle = async () => {
    setBusy(true);
    try {
      await api.post("/fleet/vehicle/me", {
        make: vehicleForm.make || null,
        model: vehicleForm.model || null,
        year: vehicleForm.year ? parseInt(vehicleForm.year, 10) : null,
        color: vehicleForm.color || null,
        plate_number: vehicleForm.plate_number || null,
        vin: vehicleForm.vin || null,
        current_odometer: vehicleForm.current_odometer ? parseFloat(vehicleForm.current_odometer) : 0,
      });
      toast.success("Vehicle saved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const logOil = async () => {
    if (!oilForm.odometer) { toast.error("Odometer required"); return; }
    setBusy(true);
    try {
      await api.post("/fleet/oil-changes", {
        driver_id: me?.id,
        oil_type: oilForm.oil_type,
        odometer: parseFloat(oilForm.odometer),
        service_date: oilForm.service_date,
        service_location: oilForm.service_location || null,
        cost: oilForm.cost ? parseFloat(oilForm.cost) : null,
        notes: oilForm.notes || null,
      });
      toast.success("Oil change logged · maintenance clock reset");
      setOilForm({ ...oilForm, odometer: "", service_location: "", cost: "", notes: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Log failed"); }
    finally { setBusy(false); }
  };

  const statusBox = () => {
    if (!maint) return null;
    const s = maint.status;
    const bg = s === "ok" ? "bg-emerald-500/10 border-emerald-500/40" :
               s === "warning" ? "bg-orange-500/10 border-orange-400/40" :
               s === "hold" ? "bg-red-500/15 border-red-500/60" :
               "bg-zinc-900 border-zinc-800";
    const Icon = s === "ok" ? CheckCircle2 : s === "hold" ? ShieldAlert : AlertTriangle;
    const tone = s === "ok" ? "text-emerald-400" : s === "hold" ? "text-red-400" : s === "warning" ? "text-orange-400" : "text-zinc-400";
    return (
      <Card className={`border-2 p-4 ${bg}`} data-testid="maint-status-box">
        <div className="flex items-start gap-3">
          <Icon className={`h-6 w-6 flex-shrink-0 mt-0.5 ${tone}`}/>
          <div className="min-w-0 flex-1">
            <div className={`font-bold text-sm uppercase tracking-wider ${tone}`}>Oil Change Status · {s.toUpperCase()}</div>
            <p className="text-xs text-zinc-300 mt-1">{maint.message}</p>
            {maint.override_active && (
              <p className="text-[11px] text-amber-400 mt-1">⚠ Supervisor override active — hold lifted until {maint.override_until_odometer?.toFixed?.(0)} miles.</p>
            )}
            {maint.last_oil_change?.service_date && (
              <p className="text-[10px] text-zinc-500 mt-1">Last change · {maint.last_oil_change.service_date} · {maint.last_oil_change.odometer?.toFixed?.(0)} mi · {maint.last_oil_change.oil_type}</p>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="vehicle-mgmt-panel">
      {statusBox()}

      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <h3 className="font-bold flex items-center gap-2 mb-3"><Car className="h-5 w-5 text-amber-400"/>My Vehicle</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Make" testid="veh-make" value={vehicleForm.make} onChange={(v) => setVehicleForm({...vehicleForm, make: v})}/>
          <Field label="Model" testid="veh-model" value={vehicleForm.model} onChange={(v) => setVehicleForm({...vehicleForm, model: v})}/>
          <Field label="Year" testid="veh-year" type="number" value={vehicleForm.year} onChange={(v) => setVehicleForm({...vehicleForm, year: v})}/>
          <Field label="Color" testid="veh-color" value={vehicleForm.color} onChange={(v) => setVehicleForm({...vehicleForm, color: v})}/>
          <Field label="Plate" testid="veh-plate" value={vehicleForm.plate_number} onChange={(v) => setVehicleForm({...vehicleForm, plate_number: v})}/>
          <Field label="VIN" testid="veh-vin" value={vehicleForm.vin} onChange={(v) => setVehicleForm({...vehicleForm, vin: v})}/>
          <Field label="Current odometer (mi)" testid="veh-odometer" type="number" step="0.1" value={vehicleForm.current_odometer} onChange={(v) => setVehicleForm({...vehicleForm, current_odometer: v})}/>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="veh-save" onClick={saveVehicle} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Save Vehicle</Button>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <h3 className="font-bold flex items-center gap-2 mb-3"><Wrench className="h-5 w-5 text-amber-400"/>Log an oil change</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Oil Type</Label>
            <Select value={oilForm.oil_type} onValueChange={(v) => setOilForm({...oilForm, oil_type: v})}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="oil-type-select"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                {OIL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Field label="Odometer at service (mi)" testid="oil-odometer" type="number" step="0.1" value={oilForm.odometer} onChange={(v) => setOilForm({...oilForm, odometer: v})}/>
          <Field label="Service date" testid="oil-date" type="date" value={oilForm.service_date} onChange={(v) => setOilForm({...oilForm, service_date: v})}/>
          <Field label="Service location" testid="oil-location" value={oilForm.service_location} onChange={(v) => setOilForm({...oilForm, service_location: v})}/>
          <Field label="Cost ($)" testid="oil-cost" type="number" step="0.01" value={oilForm.cost} onChange={(v) => setOilForm({...oilForm, cost: v})}/>
          <div className="sm:col-span-2">
            <Field label="Notes" testid="oil-notes" value={oilForm.notes} onChange={(v) => setOilForm({...oilForm, notes: v})}/>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="oil-submit" onClick={logOil} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Log Oil Change</Button>
        </div>
      </Card>

      {oilHistory.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-4">
          <h3 className="font-bold flex items-center gap-2 mb-2"><History className="h-4 w-4 text-amber-400"/>Oil change history</h3>
          <div className="space-y-1">
            {oilHistory.map(o => (
              <div key={o.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold">{o.service_date}</span>
                  <Badge variant="outline" className="border-zinc-700 text-[10px] uppercase">{(o.oil_type || "").replace(/_/g," ")}</Badge>
                  <span className="text-amber-400">{o.odometer?.toFixed?.(0)} mi → next due {o.next_due_odometer?.toFixed?.(0)} mi</span>
                </div>
                {o.service_location && <div className="text-[10px] text-zinc-500">{o.service_location}{o.cost ? ` · ${fmtUsd(o.cost)}` : ""}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-zinc-950 border-zinc-700 text-zinc-100 mt-1"
      />
    </div>
  );
}
